import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { FullStory } from "@fullstory/browser";
import {
  BoxIcon,
  CheckCircledIcon,
  EnvelopeClosedIcon,
} from "@radix-ui/react-icons";
import { Button, Flex, Text } from "@radix-ui/themes";
import { debounce } from "lodash";
import { useQueryClient } from "react-query";

import { useDrawerContext } from "./DrawerContext";
import { useToastContext } from "./ToastContext";
import {
  ContactRequest,
  ContactSupplierRequest,
  CreateNewSampleRequestRequest,
  RawMaterial,
  SampleRequest,
  UpdateSampleRequestRequest,
  UpsertSampleRequestRawMaterialRequest,
  VerificationStatus,
} from "../api/public";
import { Modal } from "../common/components";
import { useAddSampleRequestRawMaterialMutation } from "../hooks/useAddSampleRequestRawMaterialMutation";
import { useContactSupplierMutation } from "../hooks/useContactSupplierMutation";
import { useCreateNewSampleRequestMutation } from "../hooks/useCreateNewSampleRequestMutation";
import { meQueryKey, useGetMe } from "../hooks/useGetMe";
import {
  pendingSampleRequestQueryKey,
  useGetPendingSampleRequestQuery,
} from "../hooks/useGetPendingSampleRequestQuery";
import { sampleRequestsQueryKey } from "../hooks/useGetSubmittedSampleRequestsQuery";
import { useRemoveSampleRequestRawMaterialMutation } from "../hooks/useRemoveSampleRequestRawMaterialMutation";
import { useSubmitSampleRequestMutation } from "../hooks/useSubmitSampleRequestMutation";
import { useUpdateSampleRequestMutation } from "../hooks/useUpdateSampleRequestMutation";
import { useUpdateSampleRequestRawMaterialMutation } from "../hooks/useUpdateSampleRequestRawMaterialMutation";
import { ContactSupplierForm } from "../pages/components/ContactSupplierForm";
import { NeedsVerifiedUser } from "../pages/components/NeedsVerification";
import { Drawer } from "../pages/layouts/Drawer";
import { ContactRequestDetail } from "../pages/SampleRequests/components/ContactRequestDetail";
import { SampleRequestDetail } from "../pages/SampleRequests/components/SampleRequestDetail";
import { SampleRequests } from "../pages/SampleRequests/SampleRequests";
import { SampleRequestsCart } from "../pages/SampleRequests/SampleRequestsCart";

type SampleRequestContextType = {
  isVerifiedUser?: boolean;
  pendingSampleRequestId?: string;
  pendingSampleRequest?: SampleRequest;
  pendingSampleRequestLoading?: boolean;
  isSubmittingSampleRequest?: boolean;

  createNewSampleRequest: (payload: CreateNewSampleRequestRequest) => void;
  addToSampleRequest: (rawMaterialId: string) => void;
  removeFromSampleRequest: (rawMaterialId: string) => void;
  onContactSupplierClick: (rawMaterial: RawMaterial) => void;
  sampleRequestHasRawMaterial: (rawMaterialId: string) => boolean;
  updateSampleRequestRawMaterial: (
    request: UpsertSampleRequestRawMaterialRequest
  ) => void;
  updateSampleRequest: (request: UpdateSampleRequestRequest) => void;
  submitSampleRequest: () => void;
  showCart: () => void;
  hideCart: () => void;
  showHistory: () => void;
  hideHistory: () => void;
  showSampleRequestDetail: (sampleRequest: SampleRequest) => void;
  hideSampleRequestDetail: () => void;
  showContactRequestDetail: (contactRequest: ContactRequest) => void;
  hideContactRequestDetail: () => void;
};

const SampleRequestContext = createContext<SampleRequestContextType>({
  createNewSampleRequest: () => {},
  addToSampleRequest: () => {},
  removeFromSampleRequest: () => {},
  onContactSupplierClick: () => {},
  sampleRequestHasRawMaterial: () => false,
  updateSampleRequestRawMaterial: () => {},
  updateSampleRequest: () => {},
  submitSampleRequest: () => {},
  showCart: () => {},
  hideCart: () => {},
  showHistory: () => {},
  hideHistory: () => {},
  showSampleRequestDetail: () => {},
  hideSampleRequestDetail: () => {},
  showContactRequestDetail: () => {},
  hideContactRequestDetail: () => {},
});

type ModalStatePayload =
  | { type: "contactRequest"; rawMaterial: RawMaterial }
  | { type: "addToSampleRequest"; rawMaterialId: string };

type ModalState = {
  open: boolean;
  payload?: ModalStatePayload;
  completionTitleCopy?: string;
  completionBodyCopy?: string;
};

export const SampleRequestContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [contactSupplierRawMaterial, setContactSupplierRawMaterial] = useState<
    RawMaterial | undefined
  >(undefined);
  const [activeSampleRequest, setActiveSampleRequest] = useState<
    SampleRequest | undefined
  >(undefined);
  const [activeContactRequest, setActiveContactRequest] = useState<
    ContactRequest | undefined
  >(undefined);

  const { data: me } = useGetMe();
  const { showToast } = useToastContext();

  const { showDrawer, closeDrawer } = useDrawerContext();

  const queryClient = useQueryClient();

  const isVerifiedUser =
    me?.verification_status === VerificationStatus.VERIFIED;
  const needsShippingDetails = me?.shipping_details === null;
  const sampleRequestsEnabled = isVerifiedUser && !needsShippingDetails;

  const { data: pendingSampleRequest, isLoading: pendingSampleRequestLoading } =
    useGetPendingSampleRequestQuery({ enabled: sampleRequestsEnabled });
  const pendingSampleRequestId = pendingSampleRequest?.id;

  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    payload: undefined,
    completionTitleCopy: undefined,
    completionBodyCopy: undefined,
  });

  const { mutateAsync: _createNewSampleRequest } =
    useCreateNewSampleRequestMutation({
      onMutate: (request) => {
        FullStory("trackEventAsync", {
          name: "AddToCart",
          properties: {
            rawMaterialId: request.raw_materials?.[0],
          },
        });
      },
    });
  const { mutateAsync: _addToSampleRequest } =
    useAddSampleRequestRawMaterialMutation(pendingSampleRequestId ?? "", {
      onMutate: async (request) => {
        FullStory("trackEventAsync", {
          name: "AddToCart",
          properties: {
            sampleRequestId: pendingSampleRequestId,
            rawMaterialId: request.raw_material_id,
          },
        });
      },
    });
  const { mutateAsync: _removeFromSampleRequest } =
    useRemoveSampleRequestRawMaterialMutation(pendingSampleRequestId ?? "", {
      onSuccess: () => {
        queryClient.invalidateQueries(pendingSampleRequestQueryKey());
      },
    });

  const { mutateAsync: _contactSupplier } = useContactSupplierMutation({
    onMutate: (variables) => {
      FullStory("trackEventAsync", {
        name: "ContactSupplier",
        properties: {
          rawMaterialId: variables.raw_material_id,
        },
      });
      return undefined;
    },
    onSuccess: () => {
      showToast({
        title: "Request Sent",
        content: "Your request has been sent to the supplier",
        variant: "info",
        duration: 3000,
        animationEffect: "slideIn",
      });
    },
    onError: () => {
      showToast({
        title: "Error",
        content:
          "Failed to send message to supplier. If the issue persists, please contact us.",
        variant: "error",
        duration: 10000,
        animationEffect: "slideIn",
      });
    },
  });

  const { mutateAsync: _upsertSampleRequestRawMaterial } =
    useUpdateSampleRequestRawMaterialMutation(pendingSampleRequestId ?? "", {
      onMutate: async (request) => {
        await queryClient.cancelQueries(pendingSampleRequestQueryKey());
        const old = queryClient.getQueryData<SampleRequest | undefined>(
          pendingSampleRequestQueryKey()
        );
        queryClient.setQueryData<SampleRequest | undefined>(
          pendingSampleRequestQueryKey(),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              selected_raw_materials: old.selected_raw_materials.map((rm) => {
                if (rm.raw_material.id === request.raw_material_id) {
                  return {
                    ...rm,
                    ...request,
                  };
                }
                return rm;
              }),
            };
          }
        );
        return old;
      },
      onError: (_error, variables, context: SampleRequest | undefined) => {
        queryClient.setQueryData<SampleRequest | undefined>(
          pendingSampleRequestQueryKey(),
          context
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(pendingSampleRequestQueryKey());
      },
    });

  const { mutateAsync: _updateSampleRequest } = useUpdateSampleRequestMutation(
    pendingSampleRequestId ?? "",
    {
      onMutate: async (request) => {
        await queryClient.cancelQueries(pendingSampleRequestQueryKey());
        const old = queryClient.getQueryData<SampleRequest | undefined>(
          pendingSampleRequestQueryKey()
        );
        queryClient.setQueryData<SampleRequest | undefined>(
          pendingSampleRequestQueryKey(),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              ...request,
            };
          }
        );
        return old;
      },
      onError: (_error, variables, context: SampleRequest | undefined) => {
        queryClient.setQueryData<SampleRequest | undefined>(
          pendingSampleRequestQueryKey(),
          context
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(pendingSampleRequestQueryKey());
        queryClient.invalidateQueries(meQueryKey);
      },
    }
  );

  const {
    mutateAsync: _submitSampleRequest,
    isLoading: isSubmittingSampleRequest,
  } = useSubmitSampleRequestMutation({
    onMutate: () => {
      FullStory("trackEventAsync", {
        name: "SubmitSampleRequest",
        properties: {
          sampleRequestId: pendingSampleRequestId,
        },
      });
    },
  });

  const submitSampleRequest = useCallback(() => {
    if (!pendingSampleRequestId || isSubmittingSampleRequest) return;
    _submitSampleRequest(pendingSampleRequestId, {
      onSuccess: () => {
        queryClient.invalidateQueries(pendingSampleRequestQueryKey());
        queryClient.invalidateQueries(sampleRequestsQueryKey());
        showToast({
          title: "Request Sent",
          content: "Your request has been sent to the supplier.",
          variant: "info",
          duration: 3000,
          animationEffect: "slideIn",
        });
        closeDrawer();
      },
      onError: () => {
        showToast({
          title: "Error",
          content:
            "Failed to submit request. If the issue persists, please contact us.",
          variant: "error",
          duration: 10000,
          animationEffect: "slideIn",
        });
      },
    });
  }, [
    _submitSampleRequest,
    closeDrawer,
    isSubmittingSampleRequest,
    pendingSampleRequestId,
    queryClient,
    showToast,
  ]);

  const createNewSampleRequest = useCallback(
    (payload: CreateNewSampleRequestRequest) => {
      _createNewSampleRequest(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(pendingSampleRequestQueryKey());
        },
      });
    },
    [_createNewSampleRequest, queryClient]
  );

  const addToSampleRequest = useCallback(
    (rawMaterialId: string) => {
      if (!sampleRequestsEnabled) {
        setModalState({
          open: true,
          payload: {
            type: "addToSampleRequest",
            rawMaterialId,
          },
          completionTitleCopy:
            isVerifiedUser && needsShippingDetails
              ? "Shipping Information Saved"
              : undefined,
          completionBodyCopy:
            isVerifiedUser && needsShippingDetails
              ? "You're all set! Click 'OK' to continue."
              : undefined,
        });
        return;
      }
      if (!pendingSampleRequest) {
        createNewSampleRequest({
          needed_by_date: new Date().toISOString(),
          project_description: "",
          raw_materials: [rawMaterialId],
          support_requests: [],
        });
        return;
      }
      _addToSampleRequest(
        {
          raw_material_id: rawMaterialId,
        } as UpsertSampleRequestRawMaterialRequest,
        {
          onSuccess: () => {
            queryClient.invalidateQueries(pendingSampleRequestQueryKey());
          },
        }
      );
    },
    [
      _addToSampleRequest,
      createNewSampleRequest,
      isVerifiedUser,
      needsShippingDetails,
      pendingSampleRequest,
      queryClient,
      sampleRequestsEnabled,
    ]
  );

  const sampleRequestHasRawMaterial = useCallback(
    (rawMaterialId: string) => {
      if (!pendingSampleRequest) return false;
      return (
        pendingSampleRequest.selected_raw_materials.findIndex(
          (rm) => rm.raw_material.id === rawMaterialId
        ) !== -1
      );
    },
    [pendingSampleRequest]
  );

  const removeFromSampleRequest = useCallback(
    (rawMaterialId: string) => {
      if (!pendingSampleRequestId) return;
      _removeFromSampleRequest(rawMaterialId);
    },
    [_removeFromSampleRequest, pendingSampleRequestId]
  );

  const contactSupplier = useCallback(
    (payload: ContactSupplierRequest) => {
      _contactSupplier(payload);
      setContactSupplierRawMaterial(undefined);
    },
    [_contactSupplier]
  );

  const handleContactSupplierClick = useCallback(
    (rawMaterial: RawMaterial) => {
      if (!isVerifiedUser) {
        setModalState({
          open: true,
          payload: {
            type: "contactRequest",
            rawMaterial,
          },
        });
        return;
      }
      setContactSupplierRawMaterial(rawMaterial);
    },
    [isVerifiedUser]
  );

  const debouncedUpsertSampleRequestRawMaterial = useMemo(
    () =>
      debounce(_upsertSampleRequestRawMaterial, 300, {
        leading: false,
        trailing: true,
      }),
    [_upsertSampleRequestRawMaterial]
  );

  const debouncedUpdateSampleRequest = useMemo(
    () =>
      debounce(_updateSampleRequest, 300, { leading: false, trailing: true }),
    [_updateSampleRequest]
  );

  const value = useMemo(() => {
    return {
      isVerifiedUser,
      pendingSampleRequestId,
      pendingSampleRequest,
      pendingSampleRequestLoading,
      isSubmittingSampleRequest,
      createNewSampleRequest,
      addToSampleRequest,
      removeFromSampleRequest,
      onContactSupplierClick: handleContactSupplierClick,
      sampleRequestHasRawMaterial,
      updateSampleRequestRawMaterial: debouncedUpsertSampleRequestRawMaterial,
      updateSampleRequest: debouncedUpdateSampleRequest,
      submitSampleRequest,
      showCart: () => showDrawer("cart"),
      hideCart: closeDrawer,
      showHistory: () => showDrawer("history"),
      hideHistory: closeDrawer,
      showSampleRequestDetail: (sampleRequest: SampleRequest) => {
        showDrawer("sampleRequestDetail");
        setActiveSampleRequest(sampleRequest);
      },
      hideSampleRequestDetail: closeDrawer,
      showContactRequestDetail: (contactRequest: ContactRequest) => {
        showDrawer("contactRequestDetail");
        setActiveContactRequest(contactRequest);
      },
      hideContactRequestDetail: closeDrawer,
    };
  }, [
    isVerifiedUser,
    pendingSampleRequestId,
    pendingSampleRequest,
    pendingSampleRequestLoading,
    isSubmittingSampleRequest,
    createNewSampleRequest,
    addToSampleRequest,
    removeFromSampleRequest,
    handleContactSupplierClick,
    sampleRequestHasRawMaterial,
    debouncedUpsertSampleRequestRawMaterial,
    debouncedUpdateSampleRequest,
    submitSampleRequest,
    closeDrawer,
    showDrawer,
  ]);

  const handleAfterVerification = useCallback(() => {
    const { payload } = modalState;
    switch (payload?.type) {
      case "addToSampleRequest":
        setModalState({ open: false });
        if (payload.rawMaterialId) {
          addToSampleRequest(payload.rawMaterialId);
        }
        break;
      case "contactRequest": {
        setModalState({ open: false });
        if (payload.rawMaterial) {
          setContactSupplierRawMaterial(payload.rawMaterial);
        }
        break;
      }
    }
  }, [addToSampleRequest, modalState]);

  const title = useMemo(() => {
    if (isVerifiedUser && needsShippingDetails) {
      return (
        <Flex gap="2" align="center">
          <EnvelopeClosedIcon color="gray" width={24} height={24} />
          <Text>Add Shipping Information</Text>
        </Flex>
      );
    }
    if (isVerifiedUser) {
      return (
        <Flex gap="2" align="center">
          <CheckCircledIcon color="green" width={24} height={24} />
          <Text>
            {modalState.completionTitleCopy ?? "Verification Complete"}
          </Text>
        </Flex>
      );
    }
    return "Verify Your Account";
  }, [isVerifiedUser, modalState.completionTitleCopy, needsShippingDetails]);

  return (
    <SampleRequestContext.Provider value={value}>
      {children}
      <Modal
        title={
          <Text size="3" wrap="wrap">
            Contact {contactSupplierRawMaterial?.supplier_name}
            {" — "}
            {contactSupplierRawMaterial?.trade_name}
          </Text>
        }
        open={!!contactSupplierRawMaterial}
        onClose={() => setContactSupplierRawMaterial(undefined)}
      >
        <ContactSupplierForm
          onSubmit={contactSupplier}
          rawMaterialId={contactSupplierRawMaterial?.id}
        />
      </Modal>
      <Modal
        title={title}
        open={modalState.open}
        onClose={() => setModalState({ open: false })}
      >
        <NeedsVerifiedUser>
          <Flex direction="column" gap="4">
            <Text size="2">
              {modalState.completionBodyCopy ??
                "Your account is now verified. Click 'OK' to continue."}
            </Text>
            <Flex justify="end">
              <Button onClick={handleAfterVerification}>OK</Button>
            </Flex>
          </Flex>
        </NeedsVerifiedUser>
      </Modal>
      <Drawer id={"cart"} title="My Cart">
        <SampleRequestsCart />
      </Drawer>
      <Drawer id={"history"} title="History">
        <SampleRequests />
      </Drawer>
      <Drawer
        id={"sampleRequestDetail"}
        onBack={() => showDrawer("history")}
        title="Sample Request Details"
      >
        {activeSampleRequest && (
          <SampleRequestDetail sampleRequest={activeSampleRequest} />
        )}
      </Drawer>
      <Drawer
        id={"contactRequestDetail"}
        onBack={() => showDrawer("history")}
        title="Contact Request Details"
      >
        {activeContactRequest && (
          <ContactRequestDetail contactRequest={activeContactRequest} />
        )}
      </Drawer>
    </SampleRequestContext.Provider>
  );
};

export const useSampleRequestContext = () => {
  const context = useContext(SampleRequestContext);
  if (!context) {
    throw new Error(
      "useSampleRequestContext must be used within a SampleRequestContextProvider"
    );
  }
  return context;
};
